import { useTranslation } from "react-i18next";
import React, { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { AcademicHierarchyService } from "../../../services/AcademicHierarchyService";
import { SubjectService } from "../../../services/SubjectService";
import { UserDetailContext } from "../../../utils/Context";
import { sort } from "../../../utils/utils";
import { Selector, SINGLE } from "../Selector";

export const getChildren = (node) => {
  const children_data_dict = { subject_children: [], level_children: [] };
  const children = Object.keys(node).filter((key) => key !== "content");
  children.forEach((c) => {
    let push_this_dict = {};
    if (node[c].content.label === "is academic subject") {
      push_this_dict = {
        name: c,
        id: node[c].content.id,
        label: node[c].content.label,
      };
      children_data_dict.subject_children.push(push_this_dict);
    } else if (node[c].content.label === "hierarchy level") {
      push_this_dict[c] = node[c];
      children_data_dict.level_children.push(push_this_dict);
    }
  });
  return children_data_dict;
};

export const getDirectParents = (hierarchy) => {
  const direct_parents = [];
  hierarchy.forEach((college) => {
    const keys = Object.keys(college);
    keys.forEach((key) => {
      if (
        key !== "content" &&
        college[key].content.label === "hierarchy level"
      ) {
        const children_data = getChildren(college[key]);
        if (children_data.subject_children.length > 0) {
          const push_this_dict = {
            name: college[key].content.name,
            id: college[key].content.id,
            label: college[key].content.label,
            children: children_data.subject_children,
          };
          direct_parents.push(push_this_dict);
        }
        if (children_data.level_children) {
          direct_parents.push(
            ...getDirectParents(children_data.level_children)
          );
        }
      }
    });
  });
  return direct_parents;
};

export const AcademicHierarchySelect = ({
  selectHandler,
  setAcLevelsWithSubjects,
  clear,
  size,
  isChart,
  placeholder,
  type,
  subjects,
  setHierarchyOptions,
  planSubjects,
}) => {
  const { t } = useTranslation();
  const userDetails = useContext(UserDetailContext);
  const [academicHierarchyOptions, setAcademicHierarchyOptions] = useState();
  const [allSubjects, setAllSubjects] = useState([]);

  useEffect(() => {
    if (userDetails.role) {
      SubjectService.getSubjects().then((subs) => {
        setAllSubjects(subs);
      });
    }
  }, [userDetails]);

  useEffect(() => {
    if (allSubjects.length > 0) {
      AcademicHierarchyService.getLevelsFull([]).then((academicHierarchy) => {
        const selectOptions = [];
        const directParentHierarchy = getDirectParents(academicHierarchy);
        setAcLevelsWithSubjects(directParentHierarchy);
        directParentHierarchy.forEach((h) => {
          if (subjects) {
            if (
              h.children.some((r) => Object.keys(subjects).indexOf(r.name) >= 0)
            ) {
              selectOptions.push({ id: h.id, label: h.name });
            }
          } else if (isChart) {
            if (
              planSubjects &&
              h.children.some((r) => planSubjects.indexOf(r.id) >= 0)
            ) {
              selectOptions.push({ id: h.id, label: h.name });
            }
          } else {
            if (!userDetails.isAC()) {
              const userSubjects = allSubjects.filter((s) =>
                userDetails.ac_subjects.includes(s.title)
              );
              if (
                h.children.some((r) => userSubjects.find((s) => s.id === r.id))
              ) {
                selectOptions.push({ id: h.id, label: h.name });
              }
            } else {
              selectOptions.push({ id: h.id, label: h.name });
            }
          }
        });
        if (setHierarchyOptions) {
          setHierarchyOptions(selectOptions);
        }
        setAcademicHierarchyOptions(
          sort(selectOptions, (option) => option.label)
        );
      });
    }
  }, [allSubjects, userDetails, subjects, planSubjects]);

  return academicHierarchyOptions ? (
    <Selector
      type={type}
      id="academic-hierarchy-select"
      label={
        isChart
          ? t("academicHierarchy.selectLevelAndDepartment")
          : t("academicHierarchy.headerTitle")
      }
      options={academicHierarchyOptions}
      selectHandler={selectHandler}
      clear={clear}
      size={size}
      placeholder={placeholder}
    />
  ) : (
    <Skeleton
      count={1}
      style={{
        width: "100%",
        height: size === "small" ? 58 : 68,
        borderRadius: 5,
      }}
    />
  );
};

export const PLOHierarchySelect = ({
  academicSubjects,
  selectHandler,
  setAcLevelsWithSubjects,
  clear,
}) => {
  const { t } = useTranslation();
  const userDetails = useContext(UserDetailContext);
  const [academicHierarchyOptions, setAcademicHierarchyOptions] = useState();

  useEffect(() => {
    if (academicSubjects.length > 0) {
      AcademicHierarchyService.getLevelsFull([]).then((academicHierarchy) => {
        const selectOptions = [];
        const directParentHierarchy = getDirectParents(academicHierarchy);
        setAcLevelsWithSubjects(directParentHierarchy);
        directParentHierarchy.forEach((h) => {
          if (!userDetails.isAC()) {
            userDetails.ac_subjects.forEach((s) => {
              const subObj = academicSubjects.find((sub) => sub.title === s);
              if (h.children.some((r) => r.id === subObj.id)) {
                selectOptions.push({ id: h.id, label: h.name });
              }
            });
          } else {
            selectOptions.push({ id: h.id, label: h.name });
          }
        });
        setAcademicHierarchyOptions(selectOptions);
        if (selectOptions.length === 1) {
          selectHandler(
            selectOptions[0].id,
            directParentHierarchy.find((s) => s.id === selectOptions[0].id)
          );
        }
      });
    }
  }, [userDetails, academicSubjects]);

  return academicHierarchyOptions ? (
    <Selector
      type={SINGLE}
      id="academic-hierarchy-select"
      label={t("academicHierarchy.headerTitle")}
      options={academicHierarchyOptions}
      selectHandler={selectHandler}
      selectedItemId={
        academicHierarchyOptions.length === 1
          ? academicHierarchyOptions[0].id
          : null
      }
      clear={clear}
    />
  ) : (
    <Skeleton
      count={1}
      style={{ width: "100%", height: 68, borderRadius: 5 }}
    />
  );
};
